import React, {useEffect} from 'react';
import {Modal, Form, Input, Select, InputNumber, message} from 'antd';
import {useMutation} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {CREATE_SORTER, CreateSorterResponse, CreateSorterVariables} from '../graphql/mutations/createSorter';
import {SorterType} from '../graphql/queries/getSorterListing';
import {SorterTypeCode} from '../../../../enums/SorterEnum';

const {Option} = Select;

interface SorterModalProps {
    visible: boolean;
    onCancel: () => void;
    onSuccess: () => void;
    editingRecord?: SorterType;
    warehouseOptions: {value: string, label: string}[];
    defaultWarehouseId?: string;
}

const SorterModal: React.FC<SorterModalProps> = ({
    visible,
    onCancel,
    onSuccess,
    editingRecord,
    warehouseOptions,
    defaultWarehouseId,
}) => {
    const {t} = useTranslation();
    const [form] = Form.useForm();
    const isEdit = !!editingRecord;
    
    // 判断是否为使用中状态
    const isInUse = editingRecord?.sorterStatus === 1;

    const [saveSorter, {loading: saving}] = useMutation<CreateSorterResponse, CreateSorterVariables>(CREATE_SORTER);

    useEffect(() => {
        if (visible) {
            if (editingRecord) {
                form.setFieldsValue({
                    sorterCode: editingRecord.sorterCode,
                    sorterName: editingRecord.sorterName,
                    sorterIp: editingRecord.sorterIp,
                    sorterPort: editingRecord.sorterPort,
                    sorterStatus: editingRecord.sorterStatus,
                    sorterType: editingRecord.sorterType,
                    // Number() 归一：Option 的 value 是 number（parseInt 过），
                    // 而服务端 Long 标量可能序列化成字符串，不归一会严格不等、下拉回显成裸 id
                    warehouseId: editingRecord.warehouseId != null
                        ? Number(editingRecord.warehouseId)
                        : undefined,
                    chuteConfig: editingRecord.chuteConfig,
                    disabledChuteConfig: editingRecord.disabledChuteConfig,
                    fallbackChuteConfig: editingRecord.fallbackChuteConfig,
                });
            } else {
                // 新增时，设置默认仓库ID
                form.setFieldsValue({
                    sorterStatus: 0,
                    sorterType: 1,
                    warehouseId: defaultWarehouseId ? Number(defaultWarehouseId) : undefined,
                });
            }
        }
    }, [visible, editingRecord, defaultWarehouseId, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            // 统一调用创建接口（新增和修改都使用同一个接口）
            await saveSorter({
                variables: {
                    input: {
                        id: editingRecord?.id ? parseInt(editingRecord.id) : undefined,
                        sorterCode: values.sorterCode,
                        sorterName: values.sorterName,
                        sorterIp: values.sorterIp,
                        sorterPort: values.sorterPort,
                        sorterStatus: values.sorterStatus,
                        sorterType: values.sorterType,
                        warehouseId: values.warehouseId,
                        chuteConfig: values.chuteConfig,
                        disabledChuteConfig: values.disabledChuteConfig,
                        fallbackChuteConfig: values.fallbackChuteConfig,
                    }
                }
            });

            message.success(isEdit ? t('wms.sorter.messages.updateSuccess') : t('wms.sorter.messages.createSuccess'));
            onSuccess();
            form.resetFields();
        } catch (error: any) {
            console.error('Failed to save sorter:', error);
            
            // 提取错误消息
            let errorMessage = isEdit ? t('wms.sorter.messages.updateFailed') : t('wms.sorter.messages.createFailed');
            
            // 优先检查 extensions.messageKey
            if (error?.graphQLErrors?.[0]?.extensions?.messageKey) {
                const messageKey = error.graphQLErrors[0].extensions.messageKey;
                const details = error.graphQLErrors[0].extensions.details;
                // 传入 details 以支持占位符替换（如 {{fallbackChuteConfig}}）；
                // 漏传会让文案把占位符原样显示给运营
                errorMessage = String(t(messageKey, details)) || messageKey;
            } else if (error?.graphQLErrors?.[0]?.message) {
                errorMessage = error.graphQLErrors[0].message;
            } else if (error?.message) {
                errorMessage = error.message;
            }
            
            message.error(errorMessage);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    // 监听分拣机类型变化
    const sorterType = Form.useWatch('sorterType', form);

    // 仅 DWS / 交叉带 / 直线分拣机 需要默认格口配置；小件快手 / 狂扫 不需要
    const isChuteConfigRequired = sorterType === SorterTypeCode.DWS
        || sorterType === SorterTypeCode.CROSS_BELT
        || sorterType === SorterTypeCode.LINEAR_SORTER;

    // 类型切换后清掉 chuteConfig 已存在的校验错误（如从 DWS 切到小件快手时不应继续报红）
    useEffect(() => {
        if (visible) {
            form.validateFields(['chuteConfig']).catch(() => { /* 类型刚变时可能失败，吞掉 */ });
        }
    }, [sorterType, visible, form]);

    // 根据分拣机类型获取提示语
    const getChuteConfigPlaceholder = () => {
        if (sorterType === 1) {
            // DWS 类型
            return t('wms.sorter.placeholders.enterChuteConfigDWS') as string;
        } else if (sorterType === 2 || sorterType === 5) {
            // 交叉带 / 直线分拣机类型（直线分拣机与交叉带一致）
            return t('wms.sorter.placeholders.enterChuteConfigCrossBelt') as string;
        }
        return t('wms.sorter.placeholders.enterChuteConfig') as string;
    };

    const getDisabledChuteConfigPlaceholder = () => {
        if (sorterType === 1) {
            // DWS 类型 - 无需提示
            return '';
        } else if (sorterType === 2 || sorterType === 5) {
            // 交叉带 / 直线分拣机类型（直线分拣机与交叉带一致）
            return t('wms.sorter.placeholders.enterDisabledChuteConfig') as string;
        }
        return t('wms.sorter.placeholders.enterDisabledChuteConfig') as string;
    };

    return (
        <Modal
            title={isEdit ? t('wms.sorter.modal.editTitle') : t('wms.sorter.modal.addTitle')}
            open={visible}
            onCancel={handleCancel}
            onOk={handleSubmit}
            confirmLoading={saving}
            width={600}
        >
            <Form
                form={form}
                layout="horizontal"
                labelAlign="left"
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
            >
                <Form.Item
                    name="sorterCode"
                    label={t('wms.sorter.modal.sorterCode')}
                    rules={[{required: true, message: t('wms.sorter.validation.sorterCodeRequired') as string}]}
                >
                    <Input placeholder={t('wms.sorter.placeholders.enterSorterCode') as string} disabled={isEdit} />
                </Form.Item>

                <Form.Item
                    name="sorterName"
                    label={t('wms.sorter.modal.sorterName')}
                    rules={[{required: true, message: t('wms.sorter.validation.sorterNameRequired') as string}]}
                >
                    <Input placeholder={t('wms.sorter.placeholders.enterSorterName') as string} />
                </Form.Item>

                <Form.Item
                    name="sorterIp"
                    label={t('wms.sorter.modal.sorterIp')}
                >
                    <Input placeholder={t('wms.sorter.placeholders.enterSorterIp') as string} />
                </Form.Item>

                <Form.Item
                    name="sorterPort"
                    label={t('wms.sorter.modal.sorterPort')}
                    rules={[{
                        // 库里是 VARCHAR 不校验取值，端口合法性只能在这里挡：仅允许 1~65535、无前导 0
                        pattern: /^([1-9]\d{0,3}|[1-5]\d{4}|6[0-4]\d{3}|65[0-4]\d{2}|655[0-2]\d|6553[0-5])$/,
                        message: t('wms.sorter.validation.sorterPortInvalid') as string,
                    }]}
                >
                    <Input placeholder={t('wms.sorter.placeholders.enterSorterPort') as string} />
                </Form.Item>

                <Form.Item
                    name="sorterStatus"
                    label={t('wms.sorter.modal.status')}
                    rules={[{required: true, message: t('wms.sorter.validation.statusRequired') as string}]}
                >
                    <Select 
                        placeholder={t('wms.sorter.placeholders.selectStatus') as string}
                        disabled={true}
                        style={{ backgroundColor: '#f5f5f5' }}
                    >
                        <Option value={0}>{t('wms.sorter.status.unused')}</Option>
                        <Option value={1}>{t('wms.sorter.status.inUse')}</Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    name="sorterType"
                    label={t('wms.sorter.modal.type')}
                    rules={[{required: true, message: t('wms.sorter.validation.typeRequired') as string}]}
                >
                    <Select 
                        placeholder={t('wms.sorter.placeholders.selectType') as string}
                        disabled={isInUse}
                        style={isInUse ? { backgroundColor: '#f5f5f5' } : undefined}
                    >
                        <Option value={1}>{t('wms.sorter.type.dws')}</Option>
                        <Option value={2}>{t('wms.sorter.type.crossBelt')}</Option>
                        <Option value={3}>{t('wms.sorter.type.smallParcelScanner')}</Option>
                        <Option value={4}>{t('wms.sorter.type.rapidScan')}</Option>
                        <Option value={5}>{t('wms.sorter.type.linearSorter')}</Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    name="warehouseId"
                    label={t('wms.sorter.modal.warehouse')}
                    rules={[{required: true, message: t('wms.sorter.validation.warehouseRequired') as string}]}
                >
                    <Select
                        placeholder={t('wms.sorter.placeholders.selectWarehouse') as string}
                        showSearch
                        optionFilterProp="children"
                        disabled
                    >
                        {warehouseOptions.map(option => (
                            <Option key={option.value} value={Number(option.value)}>
                                {option.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="chuteConfig"
                    label={t('wms.sorter.modal.chuteConfig')}
                    required={isChuteConfigRequired}
                    dependencies={['sorterType']}
                    rules={
                        isChuteConfigRequired
                            ? [{required: true, message: t('wms.sorter.validation.chuteConfigRequired') as string}]
                            : []
                    }
                >
                    <Input.TextArea
                        placeholder={getChuteConfigPlaceholder()}
                        rows={3}
                        disabled={isEdit}
                        style={isEdit ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined}
                    />
                </Form.Item>

                <Form.Item
                    name="disabledChuteConfig"
                    label={t('wms.sorter.modal.disabledChuteConfig')}
                >
                    <Input.TextArea 
                        placeholder={getDisabledChuteConfigPlaceholder()}
                        rows={3}
                        disabled={isEdit}
                        style={isEdit ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined}
                    />
                </Form.Item>

                <Form.Item
                    name="fallbackChuteConfig"
                    label={t('wms.sorter.modal.fallbackChuteConfig')}
                >
                    <Input.TextArea
                        placeholder={t('wms.sorter.modal.fallbackChuteConfigPlaceholder') as string}
                        rows={2}
                        disabled={isEdit}
                        style={isEdit ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default SorterModal;

